import { useEffect, useMemo, useState } from "react";
import {
    Button, Card, Col, ConfigProvider, DatePicker, Empty, Row, Segmented,
    Select, Spin, Statistic, Table, Tag, Typography,
} from "antd";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import { FileExcelOutlined, FilePdfOutlined, WalletOutlined, BankOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "react-toastify";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import axiosInstance from "../../variables/api/axiosInstance";
import { displayNumbers } from "../../variables/numbersFunctions";
import {
    loadEntete, loadLogos, loadLogoCompagnieConnectee,
    drawEtatHeaderFooter, finalizeEtatPdf, NAVY,
} from "../stats/sinistre/etatsSinistresFunctions";

const { RangePicker } = DatePicker;
const PRIMARY = "#242166";

const fcfa = (v) => `${displayNumbers(Math.trunc(Number(v) || 0))} F`;
// Séparateur de milliers compatible jsPDF : fr-FR produit U+202F, rendu « / » par jsPDF.
const pdfMontant = (v) => new Intl.NumberFormat("en-DE").format(Math.trunc(Number(v) || 0)).replace(/\./g, " ");

/**
 * ÉTAT DE CAISSE PAR CAISSIER — TOTAL de ce qu'une caissière a ENCAISSÉ, quelle que soit
 * la nature de l'entrée. Les deux sources sont deux formes d'un MÊME acte d'encaissement,
 * jusqu'ici consultables sur deux écrans distincts :
 *   A. ENCAISSEMENTS DE PRIMES                        (/encaissements/v2)
 *   B. ENCAISSEMENTS DE BORDEREAUX DE REVERSEMENT     (/borderaux-de-versement-en-compagnie)
 *
 * ATTENTION au sens de B : ce ne sont PAS des sorties de caisse. La caissière (surtout au
 * Siège) encaisse les bordereaux de reversement apportés par les AUTRES agences — c'est de
 * l'argent qui ENTRE. Le total recherché est donc une SOMME (primes + bordereaux), pas une
 * différence : on veut savoir combien elle a encaissé au total.
 *
 * Identité du caissier : on filtre les DEUX sources avec la même valeur — la liste des
 * code_id d'une personne, telle que fournie par /api/reglement/caissiers (qui regroupe par
 * personne_code_id). C'est indispensable : m_utilisateur est historisée et son code_id
 * CHANGE d'une version à l'autre, donc ni un code_id isolé ni le nom seul (homonymes) ne
 * désignent une personne de façon stable dans le temps.
 *
 * Périmètre des bordereaux : on ne retient que ceux dont la DATE D'ENCAISSEMENT (versement)
 * tombe dans la période affichée, pour que le total corresponde à l'activité de caisse de
 * la période.
 */
const EtatCaissier = () => {
    const [periode, setPeriode] = useState([dayjs(), dayjs()]);
    const [caissiers, setCaissiers] = useState([]);
    const [caissierFilter, setCaissierFilter] = useState(null); // valeur = liste de code_id
    const [mode, setMode] = useState("PERIODE");                // PERIODE | JOURNALIER
    const [loading, setLoading] = useState(false);
    const [donnees, setDonnees] = useState([]);                 // [{ nom, encaissements[], versements[] }]
    const [exporting, setExporting] = useState(false);

    // Référentiel des caissiers : source unique pour les deux flux (cf. javadoc de classe).
    useEffect(() => {
        axiosInstance.get(`/api/reglement/caissiers`)
            .then((r) => setCaissiers(Array.isArray(r.data) ? r.data : []))
            .catch((e) => console.log(e));
    }, []);

    const debut = periode?.[0] ? periode[0].format("YYYY-MM-DD") : null;
    const fin = periode?.[1] ? periode[1].format("YYYY-MM-DD") : null;

    // ── Chargement : les deux sources, filtrées par la MÊME valeur de caissier ──
    useEffect(() => {
        if (!debut || !fin) return;
        let annule = false;
        setLoading(true);

        const cibles = caissierFilter
            ? caissiers.filter((c) => c.codeId === caissierFilter)
            : caissiers;

        (async () => {
            try {
                const resultats = await Promise.all(cibles.map(async (c) => {
                    const [encRes, borRes] = await Promise.all([
                        axiosInstance.get(`/api/reglement`, {
                            params: {
                                page: 0, size: 100000, search: "",
                                startDate: debut, endDate: fin,
                                compagnie: "", statut: null, modePaiement: null,
                                caissier: c.codeId,
                            },
                        }),
                        axiosInstance.get(`/api/bordereau/page`, {
                            params: {
                                page: 0, size: 100000,
                                startDate: debut, endDate: fin,
                                statut: "VERSE", caissier: c.codeId,
                            },
                        }),
                    ]);
                    // Tri commun : date d'encaissement DÉCROISSANTE (le plus récent en tête).
                    // Indispensable pour B : /api/bordereau/page ordonne par created_at (date
                    // d'ÉDITION), pas par date d'encaissement → l'ordre reçu ne suit pas la
                    // colonne Date affichée. On réordonne les deux flux de la même façon pour un
                    // rendu cohérent (aperçu, PDF, Excel).
                    const parDateDesc = (champ) => (x, y) =>
                        dayjs(y[champ]).valueOf() - dayjs(x[champ]).valueOf();
                    // Encaissements RÉELS : une ligne non réglée (pas de mode ni de date) est
                    // une prime attendue, pas de l'argent entré en caisse.
                    const encaissements = (encRes.data?.content || [])
                        .filter((r) => r.dateReglement && r.modePaiement && r.modePaiement !== "-")
                        .sort(parDateDesc("dateReglement"));
                    const versements = (borRes.data?.content || [])
                        .filter((b) => b.dateReglement)
                        .sort(parDateDesc("dateReglement"));
                    return { nom: c.nom, codeId: c.codeId, encaissements, versements };
                }));
                if (annule) return;
                // On ne garde que les caissiers ayant au moins un mouvement : un état de caisse
                // listant 30 agents à zéro serait illisible.
                setDonnees(resultats.filter((r) => r.encaissements.length || r.versements.length));
            } catch (e) {
                console.log(e);
                if (!annule) { setDonnees([]); toast.error("Erreur lors du chargement de l'état de caisse."); }
            } finally {
                if (!annule) setLoading(false);
            }
        })();

        return () => { annule = true; };
    }, [debut, fin, caissierFilter, caissiers]);

    // ── Totaux ──
    const totaux = useMemo(() => donnees.reduce((acc, d) => {
        const enc = d.encaissements.reduce((s, r) => s + (Number(r.montant) || 0), 0);
        const ver = d.versements.reduce((s, b) => s + (Number(b.montantTotal) || 0), 0);
        return { encaisse: acc.encaisse + enc, verse: acc.verse + ver };
    }, { encaisse: 0, verse: 0 }), [donnees]);

    const optionsCaissiers = useMemo(
        () => caissiers.map((c) => ({ value: c.codeId, label: c.nom })), [caissiers]);

    const libellePeriode = debut && fin
        ? `du ${dayjs(debut).format("DD/MM/YYYY")} au ${dayjs(fin).format("DD/MM/YYYY")}`
        : "toutes périodes";

    /**
     * Découpe les mouvements d'un caissier en GROUPES d'affichage :
     * mode journalier → un groupe par jour ; mode période → un seul groupe.
     * Le regroupement se fait sur la date du mouvement (règlement / versement).
     */
    const groupesDe = (d) => {
        if (mode !== "JOURNALIER") {
            return [{ cle: libellePeriode, encaissements: d.encaissements, versements: d.versements }];
        }
        const parJour = new Map();
        const ajouter = (jour, champ, ligne) => {
            if (!parJour.has(jour)) parJour.set(jour, { encaissements: [], versements: [] });
            parJour.get(jour)[champ].push(ligne);
        };
        d.encaissements.forEach((r) => ajouter(dayjs(r.dateReglement).format("YYYY-MM-DD"), "encaissements", r));
        d.versements.forEach((b) => ajouter(dayjs(b.dateReglement).format("YYYY-MM-DD"), "versements", b));
        return [...parJour.keys()].sort().map((j) => ({
            cle: dayjs(j).format("DD/MM/YYYY"),
            encaissements: parJour.get(j).encaissements,
            versements: parJour.get(j).versements,
        }));
    };

    const somme = (lignes, champ) => lignes.reduce((s, l) => s + (Number(l[champ]) || 0), 0);

    // ── Export PDF ──
    const exportPDF = async () => {
        if (!donnees.length) { toast.info("Aucun mouvement sur la période."); return; }
        setExporting(true);
        try {
            const doc = new jsPDF({ orientation: "portrait" });
            let enteteData = null, logos = [], logoCompagnie = null;
            try {
                [enteteData, logos, logoCompagnie] = await Promise.all(
                    [loadEntete(), loadLogos(), loadLogoCompagnieConnectee()]);
            } catch (e) { /* entête/logos optionnels */ }
            const formattedNow = new Date().toLocaleString("fr-FR", {
                day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
            });

            const body = [];
            donnees.forEach((d) => {
                body.push([{ content: `CAISSIER : ${d.nom}`, colSpan: 6,
                    styles: { fillColor: NAVY, textColor: 255, fontStyle: "bold" } }]);

                groupesDe(d).forEach((g) => {
                    if (mode === "JOURNALIER") {
                        body.push([{ content: g.cle, colSpan: 6,
                            styles: { fillColor: [219, 223, 240], textColor: 20, fontStyle: "bold" } }]);
                    }
                    // A. Encaissements
                    body.push([{ content: "A. Encaissements de primes", colSpan: 6,
                        styles: { fillColor: [240, 242, 250], fontStyle: "bold" } }]);
                    if (!g.encaissements.length) {
                        body.push([{ content: "Aucun encaissement", colSpan: 6, styles: { textColor: 130 } }]);
                    } else {
                        g.encaissements.forEach((r) => body.push([
                            r.dateReglement ? dayjs(r.dateReglement).format("DD/MM/YYYY") : "-",
                            r.numeroRecu || "-",
                            r.numeroPolice || "-",
                            `${r.nomSouscripteur || ""} ${r.prenomSouscripteur || ""}`.trim() || "-",
                            (r.modePaiement && r.modePaiement !== "-") ? r.modePaiement : "-",
                            { content: pdfMontant(r.montant), styles: { halign: "right" } },
                        ]));
                    }
                    body.push([
                        { content: `Sous-total encaissé (${g.encaissements.length})`, colSpan: 5,
                            styles: { halign: "right", fontStyle: "bold", fillColor: [240, 242, 250] } },
                        { content: pdfMontant(somme(g.encaissements, "montant")),
                            styles: { halign: "right", fontStyle: "bold", fillColor: [240, 242, 250] } },
                    ]);

                    // B. Versements
                    body.push([{ content: "B. Encaissements de bordereaux de reversement", colSpan: 6,
                        styles: { fillColor: [240, 242, 250], fontStyle: "bold" } }]);
                    if (!g.versements.length) {
                        body.push([{ content: "Aucun bordereau encaissé", colSpan: 6, styles: { textColor: 130 } }]);
                    } else {
                        g.versements.forEach((b) => body.push([
                            b.dateReglement ? dayjs(b.dateReglement).format("DD/MM/YYYY") : "-",
                            "-", // pas de reçu client sur un bordereau de reversement
                            b.numeroBordereau || "-",
                            b.agenceNom || b.observation || "-",
                            b.modePaiement || "-",
                            { content: pdfMontant(b.montantTotal), styles: { halign: "right" } },
                        ]));
                    }
                    body.push([
                        { content: `Sous-total bordereaux (${g.versements.length})`, colSpan: 5,
                            styles: { halign: "right", fontStyle: "bold", fillColor: [240, 242, 250] } },
                        { content: pdfMontant(somme(g.versements, "montantTotal")),
                            styles: { halign: "right", fontStyle: "bold", fillColor: [240, 242, 250] } },
                    ]);

                    // Total du groupe = primes + bordereaux (les deux sont des encaissements).
                    const totalGroupe = somme(g.encaissements, "montant") + somme(g.versements, "montantTotal");
                    body.push([
                        { content: "TOTAL ENCAISSÉ (primes + bordereaux)", colSpan: 5,
                            styles: { halign: "right", fontStyle: "bold", fillColor: [232, 234, 246] } },
                        { content: pdfMontant(totalGroupe),
                            styles: { halign: "right", fontStyle: "bold", fillColor: [232, 234, 246] } },
                    ]);
                });
            });

            autoTable(doc, {
                head: [["Date", "N° Reçu", "N° Police / Bordereau", "Assuré / Apporteur", "Mode", "Montant"]],
                body,
                foot: [[
                    { content: `TOTAL GÉNÉRAL — primes ${pdfMontant(totaux.encaisse)} · bordereaux ${pdfMontant(totaux.verse)}`,
                        colSpan: 5, styles: { halign: "right", fontStyle: "bold" } },
                    { content: pdfMontant(totaux.encaisse + totaux.verse),
                        styles: { halign: "right", fontStyle: "bold" } },
                ]],
                theme: "grid",
                styles: { fontSize: 8, cellPadding: 1.6, overflow: "linebreak", valign: "middle" },
                headStyles: { fillColor: NAVY, textColor: 255, fontSize: 8, fontStyle: "bold" },
                footStyles: { fillColor: [243, 244, 246], textColor: 20 },
                margin: { top: 32, bottom: 22, left: 14, right: 14 },
                columnStyles: { 0: { cellWidth: 22, halign: "center" }, 1: { cellWidth: 26 }, 5: { cellWidth: 28, halign: "right" } },
                didDrawPage: () => drawEtatHeaderFooter(doc, {
                    title: "ÉTAT DE CAISSE PAR CAISSIER",
                    subtitle: `${mode === "JOURNALIER" ? "Journalier" : "Période"} · ${libellePeriode}`
                        + `${caissierFilter ? ` · ${donnees[0]?.nom || ""}` : ""}`,
                    enteteData, logos, formattedNow, logoCompagnie,
                }),
            });
            finalizeEtatPdf(doc);
            doc.save(`etat-caisse-caissier-${debut}_${fin}.pdf`);
        } catch (e) {
            console.log(e);
            toast.error("Erreur lors de la génération du PDF.");
        } finally {
            setExporting(false);
        }
    };

    // ── Export Excel : une feuille par caissier (encaissements puis versements) ──
    const exportExcel = () => {
        if (!donnees.length) { toast.info("Aucun mouvement sur la période."); return; }
        const wb = XLSX.utils.book_new();
        const prises = new Set();
        // Nom de feuille : 31 caractères max, []:*?/\ interdits, unicité obligatoire.
        const nomFeuille = (nom) => {
            let n = (nom || "Caissier").replace(/[[\]:*?/\\]/g, " ").replace(/\s+/g, " ").slice(0, 31).trim();
            let i = 2;
            while (prises.has(n)) { const s = `~${i++}`; n = `${n.slice(0, 31 - s.length)}${s}`; }
            prises.add(n);
            return n;
        };

        const synthese = donnees.map((d) => ({
            Caissier: d.nom,
            "Nb primes": d.encaissements.length,
            "Primes encaissées": Math.trunc(somme(d.encaissements, "montant")),
            "Nb bordereaux": d.versements.length,
            "Bordereaux encaissés": Math.trunc(somme(d.versements, "montantTotal")),
            "TOTAL encaissé": Math.trunc(somme(d.encaissements, "montant") + somme(d.versements, "montantTotal")),
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(synthese), nomFeuille("Synthèse"));

        donnees.forEach((d) => {
            const aoa = [[d.nom], [libellePeriode], []];
            aoa.push(["A. ENCAISSEMENTS DE PRIMES"]);
            aoa.push(["Date", "N° Reçu", "N° Police", "Assuré", "Avenant", "Mode", "Montant"]);
            d.encaissements.forEach((r) => aoa.push([
                r.dateReglement ? dayjs(r.dateReglement).format("DD/MM/YYYY") : "",
                r.numeroRecu || "", r.numeroPolice || "", `${r.nomSouscripteur || ""} ${r.prenomSouscripteur || ""}`.trim(),
                r.avenant || "AN", r.modePaiement || "", Math.trunc(Number(r.montant) || 0),
            ]));
            aoa.push(["", "", "", "", "", "Sous-total encaissé", Math.trunc(somme(d.encaissements, "montant"))], []);

            aoa.push(["B. ENCAISSEMENTS DE BORDEREAUX DE REVERSEMENT"]);
            aoa.push(["Date", "N° Bordereau", "Apporteur", "Compagnie", "Mode", "Montant"]);
            d.versements.forEach((b) => aoa.push([
                b.dateReglement ? dayjs(b.dateReglement).format("DD/MM/YYYY") : "",
                b.numeroBordereau || "", b.agenceNom || b.observation || "",
                b.organismeNom || "", b.modePaiement || "", Math.trunc(Number(b.montantTotal) || 0),
            ]));
            aoa.push(["", "", "", "", "Sous-total bordereaux", Math.trunc(somme(d.versements, "montantTotal"))], []);
            aoa.push(["", "", "", "", "TOTAL ENCAISSÉ (primes + bordereaux)",
                Math.trunc(somme(d.encaissements, "montant") + somme(d.versements, "montantTotal"))]);

            XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), nomFeuille(d.nom));
        });

        XLSX.writeFile(wb, `etat-caisse-caissier-${debut}_${fin}.xlsx`);
    };

    // ── Colonnes d'aperçu ──
    const colEncaissements = [
        { title: "Date", dataIndex: "dateReglement", width: 110, render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "—") },
        { title: "N° Reçu", dataIndex: "numeroRecu", width: 140, render: (v) => v || "—" },
        { title: "N° Police", dataIndex: "numeroPolice", render: (v) => v || "—" },
        { title: "Assuré", key: "a", render: (_, r) => `${r.nomSouscripteur || ""} ${r.prenomSouscripteur || ""}`.trim() || "—" },
        { title: "Mode", dataIndex: "modePaiement", width: 110, render: (v) => v || "—" },
        { title: "Montant", dataIndex: "montant", align: "right", width: 130, render: (v) => <strong>{fcfa(v)}</strong> },
    ];
    const colVersements = [
        { title: "Date", dataIndex: "dateReglement", width: 110, render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "—") },
        { title: "N° Bordereau", dataIndex: "numeroBordereau", render: (v) => v || "—" },
        { title: "Apporteur", key: "ap", render: (_, b) => b.agenceNom || b.observation || "—" },
        { title: "Mode", dataIndex: "modePaiement", width: 110, render: (v) => v || "—" },
        { title: "Montant", dataIndex: "montantTotal", align: "right", width: 130, render: (v) => <strong>{fcfa(v)}</strong> },
    ];

    return (
        <ConfigProvider locale={frFR} theme={{ token: { colorPrimary: PRIMARY } }}>
            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">
                    <SideBar currentMenu={"CAISSE"} />
                    <div className="layout-page">
                        <NavBar />
                        <div className="content-wrapper">
                            <div className="container-xxl flex-grow-1 container-p-y">
                            <Card
                                style={{ borderRadius: 12, marginBottom: 16 }}
                                title={
                                    <div>
                                        <Typography.Title level={4} style={{ margin: 0, color: PRIMARY }}>
                                            État de caisse par caissier
                                        </Typography.Title>
                                        <Typography.Text type="secondary">
                                            Encaissements de primes et versements en compagnie, dans un seul document
                                        </Typography.Text>
                                    </div>
                                }
                                extra={
                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                        <Button icon={<FileExcelOutlined />} onClick={exportExcel}
                                            disabled={!donnees.length || loading}
                                            style={{ borderColor: "#059669", color: "#059669" }}>Excel</Button>
                                        <Button icon={<FilePdfOutlined />} onClick={exportPDF}
                                            loading={exporting} disabled={!donnees.length || loading}
                                            style={{ borderColor: "#dc2626", color: "#dc2626" }}>PDF</Button>
                                    </div>
                                }
                            >
                                <Row gutter={[12, 12]} align="middle">
                                    <Col xs={24} md={9}>
                                        <RangePicker style={{ width: "100%" }} format="DD/MM/YYYY"
                                            value={periode} onChange={(v) => setPeriode(v)} allowClear={false} />
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Select allowClear showSearch optionFilterProp="label"
                                            style={{ width: "100%" }} placeholder="Tous les caissiers"
                                            options={optionsCaissiers} value={caissierFilter}
                                            onChange={(v) => setCaissierFilter(v || null)} />
                                    </Col>
                                    <Col xs={24} md={7}>
                                        <Segmented block value={mode} onChange={setMode}
                                            options={[
                                                { label: "Période", value: "PERIODE" },
                                                { label: "Journalier", value: "JOURNALIER" },
                                            ]} />
                                    </Col>
                                </Row>

                                <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
                                    <Col xs={24} md={8}>
                                        <Card size="small" style={{ borderRadius: 10 }}>
                                            <Statistic title="Primes encaissées" value={fcfa(totaux.encaisse)}
                                                prefix={<WalletOutlined style={{ color: "#16a34a" }} />}
                                                valueStyle={{ color: "#16a34a", fontSize: 20 }} />
                                        </Card>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Card size="small" style={{ borderRadius: 10 }}>
                                            <Statistic title="Bordereaux encaissés (autres agences)" value={fcfa(totaux.verse)}
                                                prefix={<BankOutlined style={{ color: PRIMARY }} />}
                                                valueStyle={{ color: PRIMARY, fontSize: 20 }} />
                                        </Card>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Card size="small" style={{ borderRadius: 10, background: "#f0f5ff" }}>
                                            <Statistic title="TOTAL ENCAISSÉ (primes + bordereaux)"
                                                value={fcfa(totaux.encaisse + totaux.verse)}
                                                valueStyle={{ color: PRIMARY, fontSize: 22, fontWeight: 700 }} />
                                        </Card>
                                    </Col>
                                </Row>
                            </Card>

                            <Spin spinning={loading} tip="Chargement des deux flux…">
                                {!donnees.length && !loading ? (
                                    <Card style={{ borderRadius: 12 }}>
                                        <Empty description="Aucun mouvement de caisse sur la période sélectionnée" />
                                    </Card>
                                ) : donnees.map((d) => (
                                    <Card key={d.codeId} style={{ borderRadius: 12, marginBottom: 16 }}
                                        title={<span style={{ color: PRIMARY, fontWeight: 700 }}>{d.nom}</span>}
                                        extra={
                                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                                <Tag color="green">Primes : {fcfa(somme(d.encaissements, "montant"))}</Tag>
                                                <Tag color="blue">Bordereaux : {fcfa(somme(d.versements, "montantTotal"))}</Tag>
                                                <Tag color="purple" style={{ fontWeight: 700 }}>
                                                    Total encaissé : {fcfa(somme(d.encaissements, "montant") + somme(d.versements, "montantTotal"))}
                                                </Tag>
                                            </div>
                                        }
                                    >
                                        {groupesDe(d).map((g) => (
                                            <div key={g.cle} style={{ marginBottom: 20 }}>
                                                {mode === "JOURNALIER" && (
                                                    <Typography.Title level={5} style={{ color: PRIMARY }}>{g.cle}</Typography.Title>
                                                )}
                                                <Typography.Text strong>A. Encaissements de primes</Typography.Text>
                                                <Table size="small" rowKey={(r, i) => `e-${g.cle}-${i}`} pagination={false}
                                                    columns={colEncaissements} dataSource={g.encaissements}
                                                    locale={{ emptyText: "Aucun encaissement" }}
                                                    summary={() => (
                                                        <Table.Summary.Row>
                                                            <Table.Summary.Cell index={0} colSpan={4} align="right">
                                                                <strong>Sous-total encaissé</strong>
                                                            </Table.Summary.Cell>
                                                            <Table.Summary.Cell index={4} align="right">
                                                                <strong>{fcfa(somme(g.encaissements, "montant"))}</strong>
                                                            </Table.Summary.Cell>
                                                        </Table.Summary.Row>
                                                    )} />
                                                <Typography.Text strong style={{ display: "block", marginTop: 12 }}>
                                                    B. Encaissements de bordereaux de reversement
                                                </Typography.Text>
                                                <Table size="small" rowKey={(b, i) => `v-${g.cle}-${i}`} pagination={false}
                                                    columns={colVersements} dataSource={g.versements}
                                                    locale={{ emptyText: "Aucun versement" }}
                                                    summary={() => (
                                                        <Table.Summary.Row>
                                                            <Table.Summary.Cell index={0} colSpan={4} align="right">
                                                                <strong>Sous-total bordereaux</strong>
                                                            </Table.Summary.Cell>
                                                            <Table.Summary.Cell index={4} align="right">
                                                                <strong>{fcfa(somme(g.versements, "montantTotal"))}</strong>
                                                            </Table.Summary.Cell>
                                                        </Table.Summary.Row>
                                                    )} />
                                            </div>
                                        ))}
                                    </Card>
                                ))}
                            </Spin>
                            </div>
                            <Footer />
                        </div>
                    </div>
                </div>
            </div>
        </ConfigProvider>
    );
};

export default EtatCaissier;
